import type { Feature } from 'geojson'

import type {
  OperationalSearchIndexRow,
  ParsedAttributeQuery,
  SmartSearchFeatureHit,
  SmartSearchLayerHit,
} from '../types'

import type { LayerSearchRow } from './buildLayerSearchRows'
import type { LayerSearchFacetFilters } from './filterLayerSearchIndex'
import {
  SMART_SEARCH_MAX_RESULTS,
  filterSearchIndex,
} from './filterSearchIndex'
import { folderFacetMatches } from './folderFacetMatch'
import { matchesAttributeHaystack } from './matchAttributeHaystack'
import { operationalRowToFeatureHit } from './operationalRowToFeatureHit'
import { hasPositiveTerms, parseAttributeQuery } from './parseAttributeQuery'

export type SmartSearchLayerGroup = {
  layerId: number
  layerName: string
  sharingKind: 'public' | 'shared'
  category: string | null
  layerFlyFeature: Feature
  layerCreatorUserId?: number | null
  layerCreatorUsername?: string | null
  totalFeaturesInLayer: number
  /** Objets correspondant à la requête ; vide en navigation (dropdown charge la liste). */
  matchedFeatures: SmartSearchFeatureHit[]
}

function facetMatchesRow(
  row: LayerSearchRow,
  facets: LayerSearchFacetFilters,
): boolean {
  return folderFacetMatches(row.category, facets.category)
}

/** Liste tous les objets d'une couche pour le détail dans la palette. */
export function featureHitsForOperationalLayer(
  layerId: number,
  indexRows: OperationalSearchIndexRow[],
): SmartSearchFeatureHit[] {
  const rows = indexRows.filter(r => r.layerId === layerId)
  return rows.map((row, i) =>
    operationalRowToFeatureHit(row, `${layerId}-obj-${i}`),
  )
}

/** Hit « couche entière » pour fly-to / popup. */
export function layerGroupToLayerHit(
  group: SmartSearchLayerGroup,
): SmartSearchLayerHit {
  return {
    kind: 'layer',
    key: `layer-${group.layerId}`,
    layerId: group.layerId,
    layerName: group.layerName,
    label: group.layerName,
    feature: group.layerFlyFeature,
    sharingKind: group.sharingKind,
    category: group.category,
    layerCreatorUserId: group.layerCreatorUserId ?? null,
    layerCreatorUsername: group.layerCreatorUsername ?? null,
  }
}

export function buildUnifiedSearchGroups(
  query: string,
  indexRows: OperationalSearchIndexRow[],
  layerRows: LayerSearchRow[],
  facets: LayerSearchFacetFilters,
): SmartSearchLayerGroup[] {
  const parsed: ParsedAttributeQuery = parseAttributeQuery(query)
  const requireText = hasPositiveTerms(parsed)

  const filteredLayers = layerRows.filter(lr => facetMatchesRow(lr, facets))
  const eligibleIds = new Set(filteredLayers.map(l => l.layerId))
  const rowsEligible = indexRows.filter(r => eligibleIds.has(r.layerId))

  const countsByLayer = new Map<number, number>()
  for (const r of rowsEligible) {
    countsByLayer.set(r.layerId, (countsByLayer.get(r.layerId) ?? 0) + 1)
  }

  const matchedByLayer = new Map<number, SmartSearchFeatureHit[]>()

  if (requireText) {
    const hits = filterSearchIndex(rowsEligible, parsed)
    for (const h of hits) {
      const arr = matchedByLayer.get(h.layerId) ?? []
      arr.push(h)
      matchedByLayer.set(h.layerId, arr)
    }
    for (const lr of filteredLayers) {
      if (!matchesAttributeHaystack(lr.haystack, parsed)) continue
      if (!matchedByLayer.has(lr.layerId)) {
        matchedByLayer.set(lr.layerId, [])
      }
    }
  } else {
    for (const lr of filteredLayers) {
      const n = countsByLayer.get(lr.layerId) ?? 0
      if (n > 0) matchedByLayer.set(lr.layerId, [])
    }
  }

  const groups: SmartSearchLayerGroup[] = []
  const MAX_GROUPS = SMART_SEARCH_MAX_RESULTS

  for (const lr of filteredLayers) {
    if (groups.length >= MAX_GROUPS) break
    const mf = matchedByLayer.get(lr.layerId)
    if (mf === undefined) continue

    groups.push({
      layerId: lr.layerId,
      layerName: lr.layerName,
      sharingKind: lr.sharingKind,
      category: lr.category,
      layerFlyFeature: lr.flyFeature,
      layerCreatorUserId: lr.layerCreatorUserId ?? null,
      layerCreatorUsername: lr.layerCreatorUsername ?? null,
      totalFeaturesInLayer: countsByLayer.get(lr.layerId) ?? 0,
      matchedFeatures: mf,
    })
  }

  groups.sort((a, b) =>
    a.layerName.localeCompare(b.layerName, 'fr', { sensitivity: 'base' }),
  )

  return groups
}
